import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CornerDownRight,
  Eye,
  ListTree,
  MessageSquare,
  Pencil,
  Reply,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { useBoardMenu } from '../../context/BoardMenuContext';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import {
  getBoardCategoryList,
  type BoardCategory,
} from '../../utils/boardCategory';
import {
  getBoardDetail,
  isBoardCategoryEnabled,
  parseBoardSettings,
  type BoardSettings,
} from '../../utils/board';
import { getPostListPath, getPostMenuId, isQnaBoardCode } from '../../utils/boardMenu';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import PostCommentSection from '../../components/board/PostCommentSection';
import {
  getPost,
  getPostEditPath,
  getPostReplyPath,
  getPostThread,
  getPostViewPath,
  type BoardPost,
} from '../../utils/post';

const PostView: React.FC = () => {
  const navigate = useNavigate();
  const {
    projectCode: projectCodeFromUrl,
    boardCode: boardCodeFromUrl,
    postCode: postCodeFromUrl,
  } = useParams<{
    projectCode: string;
    boardCode: string;
    postCode: string;
  }>();

  const { boards } = useBoardMenu();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const [post, setPost] = useState<BoardPost | null>(null);
  const [threadPosts, setThreadPosts] = useState<BoardPost[]>([]);
  const [categories, setCategories] = useState<BoardCategory[]>([]);
  const [boardSettings, setBoardSettings] = useState<BoardSettings | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const menuBoard = boards.find((b) => b.code === boardCodeFromUrl);
  const settings = boardSettings ?? parseBoardSettings(menuBoard?.settings);
  const categoryEnabled = isBoardCategoryEnabled(menuBoard?.settings);
  const showReaction =
    settings.reaction.includes('LIKE') || settings.reaction.includes('DISLIKE');
  const showLike = settings.reaction.includes('LIKE');
  const showDislike = settings.reaction.includes('DISLIKE');
  const showViewCount = settings.view_count;
  const commentEnabled = settings.comment;
  const replyEnabled = settings.reply;
  const maxReplyDepth = settings.max_reply_depth > 0 ? settings.max_reply_depth : 5;

  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.code, c.name));
    return map;
  }, [categories]);

  const listPath =
    projectCodeFromUrl && boardCodeFromUrl
      ? getPostListPath(projectCodeFromUrl, boardCodeFromUrl)
      : '/projects';

  const boardTitle = menuBoard?.name ?? boardCodeFromUrl ?? '';

  const canReply =
    replyEnabled &&
    post != null &&
    (post.depth ?? 0) < maxReplyDepth;

  const showThreadList = threadPosts.length > 0;

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  const loadData = useCallback(async () => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !postCodeFromUrl) return;

    setIsLoading(true);
    setError(null);

    try {
      const [postData, board] = await Promise.all([
        getPost(projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl),
        getBoardDetail(projectCodeFromUrl, boardCodeFromUrl),
      ]);

      const parsedSettings = board?.settings ?? parseBoardSettings(menuBoard?.settings);
      setPost(postData);
      setBoardSettings(parsedSettings);

      const thread = await getPostThread(
        projectCodeFromUrl,
        boardCodeFromUrl,
        postCodeFromUrl,
        true
      );
      setThreadPosts(thread.posts);

      if (isBoardCategoryEnabled(board?.settings ?? menuBoard?.settings)) {
        const cats = await getBoardCategoryList(projectCodeFromUrl, boardCodeFromUrl);
        setCategories(cats.filter((c) => c.status === 'ACTIVE'));
      } else {
        setCategories([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '게시물을 불러오지 못했습니다.');
      setPost(null);
      setThreadPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl, menuBoard?.settings]);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !postCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
    setActiveMenu(
      isQnaBoardCode(boardCodeFromUrl) ? 'qna' : getPostMenuId(boardCodeFromUrl)
    );
  }, [projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openEdit = () => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !postCodeFromUrl) return;
    navigate(getPostEditPath(projectCodeFromUrl, boardCodeFromUrl, postCodeFromUrl));
  };

  const openReply = () => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !post?.code) return;
    navigate(getPostReplyPath(projectCodeFromUrl, boardCodeFromUrl, post.code));
  };

  const openThreadPost = (code: string) => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || code === postCodeFromUrl) return;
    navigate(getPostViewPath(projectCodeFromUrl, boardCodeFromUrl, code));
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
      </div>
    );
  }

  const categoryLabel =
    post?.categoryCode != null
      ? categoryNameMap.get(post.categoryCode) ?? post.categoryCode
      : null;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="게시물 보기"
          subtitle={
            boardCodeFromUrl
              ? `${boardTitle} (${boardCodeFromUrl})`
              : '게시물 상세'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate(listPath)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                게시물 목록으로
              </button>
              {post && !isLoading && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={openEdit}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil className="w-4 h-4" />
                    수정
                  </button>
                  {canReply && (
                    <button
                      type="button"
                      onClick={openReply}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      <Reply className="w-4 h-4" />
                      답글
                    </button>
                  )}
                </div>
              )}
            </div>
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
                <button
                  type="button"
                  onClick={() => loadData()}
                  className="ml-3 underline font-medium"
                >
                  다시 시도
                </button>
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : post ? (
              <>
                {/* 1. 클릭한 글 본문 */}
                <article className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {categoryEnabled && categoryLabel && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {categoryLabel}
                        </span>
                      )}
                      {post.pin != null && post.pin > 0 && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          고정
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{post.status}</span>
                      {(post.depth ?? 0) > 0 && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                          답글
                        </span>
                      )}
                    </div>
                    <h1 className="text-xl font-semibold text-gray-900">
                      {post.title || '(제목 없음)'}
                    </h1>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span>{formatDateTimeFromApi(post.createdAt)}</span>
                      {post.createdBy && <span>작성자 {post.createdBy}</span>}
                      {showReaction && (
                        <span className="inline-flex items-center gap-3">
                          {showLike && (
                            <span className="inline-flex items-center gap-1">
                              <ThumbsUp className="w-4 h-4 text-gray-400" />
                              {post.likeCount}
                            </span>
                          )}
                          {showDislike && (
                            <span className="inline-flex items-center gap-1">
                              <ThumbsDown className="w-4 h-4 text-gray-400" />
                              {post.dislikeCount}
                            </span>
                          )}
                        </span>
                      )}
                      {showViewCount && (
                        <span className="inline-flex items-center gap-1">
                          <Eye className="w-4 h-4 text-gray-400" />
                          {post.viewCount}
                        </span>
                      )}
                      {commentEnabled && (
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare className="w-4 h-4 text-gray-400" />
                          {post.commentCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-4 lg:px-6 py-6">
                    <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap break-words">
                      {post.content || '(내용 없음)'}
                    </div>
                  </div>
                </article>

                {/* 2. 스레드 목록 */}
                {showThreadList && (
                  <section className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-4 lg:px-6 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <ListTree className="w-4 h-4" />
                        스레드 ({threadPosts.length})
                      </h3>
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {threadPosts.map((threadPost) => {
                        const isCurrent = threadPost.code === postCodeFromUrl;
                        const depth = threadPost.depth ?? 0;
                        const pad = Math.min(depth, 5) * 16;
                        const isRoot = depth === 0;

                        const rowContent = (
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            {!isRoot && (
                              <CornerDownRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                {isRoot && (
                                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                                    원글
                                  </span>
                                )}
                                {isCurrent && (
                                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800">
                                    현재 글
                                  </span>
                                )}
                                <p
                                  className={`text-sm truncate ${
                                    isCurrent
                                      ? 'font-semibold text-blue-900'
                                      : 'font-medium text-gray-900'
                                  }`}
                                >
                                  {threadPost.title || '(제목 없음)'}
                                </p>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">
                                {formatDateTimeFromApi(threadPost.createdAt)}
                                {threadPost.createdBy && (
                                  <span className="ml-2">· {threadPost.createdBy}</span>
                                )}
                              </p>
                            </div>
                          </div>
                        );

                        return (
                          <li key={threadPost.code}>
                            {isCurrent ? (
                              <div
                                className="px-4 lg:px-6 py-4 bg-blue-50 border-l-4 border-blue-600"
                                style={{ paddingLeft: `${16 + pad}px` }}
                                aria-current="page"
                              >
                                {rowContent}
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openThreadPost(threadPost.code)}
                                className="w-full text-left px-4 lg:px-6 py-4 hover:bg-gray-50 transition-colors"
                                style={{ paddingLeft: `${16 + pad}px` }}
                              >
                                {rowContent}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                {commentEnabled &&
                  projectCodeFromUrl &&
                  boardCodeFromUrl &&
                  postCodeFromUrl && (
                    <PostCommentSection
                      projectCode={projectCodeFromUrl}
                      boardCode={boardCodeFromUrl}
                      postCode={postCodeFromUrl}
                      commentCount={post.commentCount}
                      onCommentCountChange={(count) =>
                        setPost((prev) => (prev ? { ...prev, commentCount: count } : prev))
                      }
                    />
                  )}
              </>
            ) : !error ? (
              <div className="text-center py-12 text-sm text-gray-500">
                게시물을 찾을 수 없습니다.
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PostView;
